// src/components/audit/AuditRow.jsx
import React from "react";
import AuditRowDetail from "./AuditRowDetails";

export default function AuditRow({
  row,
  isExpanded,
  toggle,
  onEditRow,
  onToast,
  user,
  onRefresh,
}) {
  const getSeverityColor = (sev) => {
    if (sev === null || sev === undefined || sev === "") return "#4c7a92";
    const s = String(sev).trim().toLowerCase();
    if (s === "ok") return "#3c6f48";
    const n = parseFloat(sev);
    if (!isNaN(n)) {
      if (n >= 7) return "#8b2f2f";
      if (n >= 6) return "#b36a1e";
      if (n >= 5) return "#a37a32";
      return "#4c7a92";
    }
    if (s.includes("high")) return "#8b2f2f";
    if (s.includes("medium")) return "#b36a1e";
    if (s.includes("low")) return "#a37a32";
    return "#4c7a92";
  };

  const displaySeverity = (sev) => {
    if (sev === null || sev === undefined || sev === "") return "—";
    const s = String(sev).trim().toLowerCase();
    if (s === "ok") return "COMPLIANT";
    return String(sev);
  };

  const statusClass = (row.status || "").toLowerCase().replace(/\s+/g, "-");

  // Deadline
  const rawDeadline = row.deadline || row.due_date || null;

  const formatDate = (val) => {
    if (!val) return "—";
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return String(val);
    return d.toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const daysLeft = (val) => {
    if (!val) return null;
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return null;
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const end = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    return Math.ceil((end - start) / 86400000);
  };

  // Couleurs Deadline
  const getDeadlineColors = (val, status) => {
    const s = String(status || "").toLowerCase();
    if (s === "ok" || s === "compliant") return { bg: "#3c6f48", fg: "#fff" };
    if (s === "resolved") return { bg: "#282727", fg: "#cfd3d7" };

    const dl = daysLeft(val);
    if (dl === null) return { bg: "#282727", fg: "#cfd3d7" };
    if (dl <= 0) return { bg: "#8b2f2f", fg: "#fff" };
    if (dl <= 3) return { bg: "#8b2f2f", fg: "#fff" };
    if (dl <= 7) return { bg: "#b36a1e", fg: "#fff" };
    if (dl <= 14) return { bg: "#b36a1e", fg: "#fff" };
    return { bg: "#282727", fg: "#fff" };
  };

  // Couleurs Status
  const getStatusColors = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "ok" || s === "compliant" || s === "resolved") return { bg: "#3c6f48", fg: "#fff" };
    if (s === "in-progress" || s === "in progress") return { bg: "#2f4a63", fg: "#fff" };
    if (s === "pending") return { bg: "#b36a1e", fg: "#fff" };
    if (s === "open") return { bg: "#8b2f2f", fg: "#fff" };
    return { bg: "#282727", fg: "#cfd3d7" };
  };

  const dl = daysLeft(rawDeadline);
  const { bg: deadlineBg, fg: deadlineFg } = getDeadlineColors(rawDeadline, row.status);
  const { bg: statusBg, fg: statusFg } = getStatusColors(row.status);

  const deadlineTitle = (() => {
    if (!rawDeadline) return "No deadline";
    if (dl === null) return String(rawDeadline);
    if (dl < 0) return `Overdue by ${Math.abs(dl)} day(s)`;
    if (dl === 0) return "Due today";
    return `Due in ${dl} day(s)`;
  })();

  return (
    <>
      <tr className="audit-row" onClick={toggle} style={{ cursor: "pointer" }}>
        <td className="audit-id-cell">
          <button
            className="audit-toggle"
            onClick={(e) => { e.stopPropagation(); toggle(); }}
            aria-label={isExpanded ? "Collapse" : "Expand"}
          >
            <i className={`bi bi-caret-${isExpanded ? "down" : "right"}-fill`} />
          </button>
          <span>{row.finding_id}</span>
        </td>

        {/* Severity */}
        <td
          className="audit-severity-cell"
          style={{ backgroundColor: getSeverityColor(row.severity) }}
        >
          <div className="audit-severity-content">{displaySeverity(row.severity)}</div>
        </td>

        <td className="audit-theme-cell">
          {row.needs_help ? (
            <i
              className="bi bi-flag-fill"
              title="Help requested"
              style={{ color: "#c77a3a", marginRight: 6, fontSize: 14, verticalAlign: "middle" }}
            />
          ) : null}
          {row.theme || "—"}
        </td>

        {/* Status avec variables CSS */}
        <td
          className={`audit-status-cell audit-status-${statusClass}`}
          title={row.status || "—"}
          style={{ "--st-bg": statusBg, "--st-fg": statusFg }}
        >
          {row.status || "—"}
        </td>

        {/* Deadline avec variables CSS */}
        <td
          className="audit-deadline-cell"
          title={deadlineTitle}
          style={{ "--dl-bg": deadlineBg, "--dl-fg": deadlineFg }}
        >
          {formatDate(rawDeadline)}
        </td>

        {/* Comments */}
        <td className="audit-comments-cell" title="Comments">
          <div className="audit-comments-icon">
            <i className="bi bi-chat-fill" />
            <span>{row.comments_count ?? 0}</span>
          </div>
        </td>
      </tr>

      {isExpanded && (
        <tr className="audit-detail-row" onClick={(e) => e.stopPropagation()}>
          <td colSpan={6}>
            <AuditRowDetail
              row={row}
              onEdit={onEditRow}
              onToast={onToast}
              user={user}
              onRefresh={() => onRefresh?.({ preserveExpanded: true })}
            />
          </td>
        </tr>
      )}
    </>
  );
}
